// SAM-1343: GET /crud/estate answers one page (`limit`, at most 1000 rows here) with `total_pages`. The Estates tree
// asked for page 1 only, so with ~3900 estates in Cap Cana everything after row 1000 was missing: a building showed
// «Estates (0)» and a group «0». Every page is read, the first one telling how many there are.

export interface CrudPage<T> {
  data?: T[];
  total_pages?: number;
}

/**
 * All the rows of a paged crud list: page 1, then the rest in parallel. Rows are kept once by `id`, in page order.
 * A failed page throws: a short list that looks complete is how the missing rows went unseen.
 */
export const fetchAllPages = async <T extends { id?: number | string | null }>(
  getPage: (page: number) => Promise<CrudPage<T>>,
): Promise<T[]> => {
  const first = await getPage(1);
  const pages = Math.max(1, Number(first?.total_pages) || 1);
  const rest = pages > 1
    ? await Promise.all(Array.from({ length: pages - 1 }, (_, i) => getPage(i + 2)))
    : [];
  const seen = new Set<number | string>();
  const rows: T[] = [];
  for (const page of [first, ...rest]) {
    for (const row of page?.data ?? []) {
      if (row.id != null) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
      }
      rows.push(row);
    }
  }
  return rows;
};
